import React, { useEffect, useRef, useState } from 'react';
import {
  AdminWidgetTitle,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminTableBody,
  AdminMetric,
} from '../../sections/AdminDashboard/shared/adminUx';

export const ADMIN_WIDGET_PERIOD_LABELS = {
  '7 Days': 'Last 7 Days',
  '14 Days': 'Last 14 Days',
  '30 Days': 'Last 30 Days',
  '90 Days': 'Last 90 Days',
  '1 Year': 'Last 1 Year',
};

const DEFAULT_PERIODS = ['7 Days', '14 Days', '30 Days', '90 Days'];

/** Widget card header — matches PerformanceWidget / admin spec card rhythm. */
export function AdminWidgetHeader({ title, children, className = '' }) {
  return (
    <div
      className={`flex items-center justify-between py-3 px-4 border-b border-mediumgray ${className}`.trim()}
    >
      <AdminWidgetTitle className="text-offblack">{title}</AdminWidgetTitle>
      {children}
    </div>
  );
}

/** Period filter — Micro field trigger (32px; Field §3) — compact widget headers. */
export function AdminWidgetPeriodFilter({
  period,
  onPeriodChange,
  periods = DEFAULT_PERIODS,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`.trim()} ref={ref}>
      <AdminFilterDropdownButton
        type="button"
        size="micro"
        onClick={() => setOpen((v) => !v)}
        className="!px-2"
      >
        <AdminTableBody>
          {ADMIN_WIDGET_PERIOD_LABELS[period] || period}
        </AdminTableBody>
        <svg
          className="w-3 h-3 shrink-0 transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </AdminFilterDropdownButton>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-10 w-full min-w-[4.5rem] max-w-[7.5rem] border border-lightgray shadow-sm">
          <div className="py-1">
            {periods.map((key) => (
              <AdminFilterMenuItem
                key={key}
                active={period === key}
                onClick={() => {
                  onPeriodChange(key);
                  setOpen(false);
                }}
              >
                {ADMIN_WIDGET_PERIOD_LABELS[key] || key}
              </AdminFilterMenuItem>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Generic widget menu — Micro field trigger (sort, actions, etc.). */
export function AdminWidgetMenuFilter({ label, items, className = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`.trim()} ref={ref}>
      <AdminFilterDropdownButton
        type="button"
        size="micro"
        onClick={() => setOpen((v) => !v)}
        className="!px-2"
      >
        <AdminTableBody>{label}</AdminTableBody>
        <svg
          className="w-3 h-3 shrink-0 transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </AdminFilterDropdownButton>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-10 w-full min-w-[4.5rem] max-w-[7.5rem] border border-lightgray shadow-sm">
          <div className="py-1">
            {items.map((item) => (
              <AdminFilterMenuItem
                key={item.key}
                active={item.active}
                onClick={() => {
                  item.onClick();
                  setOpen(false);
                }}
              >
                {item.label}
              </AdminFilterMenuItem>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** KPI change row — metric micro (§6), no percent suffix. */
export function AdminWidgetMetricDelta({ value, trend, className = '' }) {
  const isUp = trend === 'up';
  return (
    <span
      className={`inline-flex items-center justify-center gap-1 ${
        isUp ? 'text-freshgreen' : 'text-alertred'
      } ${className}`.trim()}
    >
      <i
        className={`fi fi-sr-triangle text-sm flex items-center justify-center ${
          isUp ? 'rotate-0' : 'rotate-180'
        }`}
        aria-hidden
      />
      <AdminMetric size="micro">
        {isUp ? '+' : '-'}
        {Math.abs(value)}
      </AdminMetric>
    </span>
  );
}
